import { createContext, useContext, useState, useEffect } from "react";

const CartContext = createContext();

const sanitizeItem = (item) => ({
  id: item.id,
  name: String(item.name || ""),
  price: parseFloat(item.price || 0),
  quantity: Number(item.quantity || 1),
  description: typeof item.description === "string" ? item.description : "",
  department: typeof item.department === "string" ? item.department : (item.department?.name || ""),
  category: typeof item.category === "string" ? item.category : (item.category?.name || ""),
});

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem("cart");
      if (!savedCart) return [];
      const parsed = JSON.parse(savedCart);
      return Array.isArray(parsed) ? parsed.map(sanitizeItem) : [];
    } catch {
      localStorage.removeItem("cart");
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("cart", JSON.stringify(cart));
    } catch {
      localStorage.removeItem("cart");
    }
  }, [cart]);

  const addToCart = (item, quantity) => {
    const qty = quantity !== undefined ? Number(quantity) : Number(item.quantity || 1);
    const normalizedItem = sanitizeItem({ ...item, quantity: qty });

    setCart((prev) => {
      const existingItem = prev.find((cartItem) => cartItem.id === normalizedItem.id);
      if (existingItem) {
        return prev.map((cartItem) =>
          cartItem.id === normalizedItem.id
            ? { ...cartItem, quantity: cartItem.quantity + qty }
            : cartItem
        );
      }
      return [...prev, normalizedItem];
    });
  };

  const updateCartItem = (itemId, newQuantity) => {
    setCart((prev) => {
      if (newQuantity <= 0) {
        return prev.filter((item) => item.id !== itemId);
      }
      return prev.map((cartItem) =>
        cartItem.id === itemId
          ? { ...cartItem, quantity: newQuantity }
          : cartItem
      );
    });
  };

  const removeCartItem = (itemId) => {
    setCart((prev) => prev.filter((item) => item.id !== itemId));
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem("cart");
  };

  return (
    <CartContext.Provider value={{ cart, addToCart, updateCartItem, removeCartItem, clearCart }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    console.error("useCart must be used within a CartProvider");
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}